import React, { useState, useCallback, useRef, useEffect } from "react";
import { useLocation, Link } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { getListRecipesQueryKey } from "@workspace/api-client-react";
import { UpdateRecipeBody } from "@workspace/api-zod";
import { isRealIngredient } from "@workspace/recipe-utils";
import type { z } from "zod";

type UpdateRecipeRequest = z.infer<typeof UpdateRecipeBody>;
import {
  ChevronLeft,
  Save,
  Loader2,
  AlertCircle,
  PlusCircle,
  FileEdit,
} from "lucide-react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import {
  IngredientsEditor,
  type LocalIngredient,
} from "@/components/validate/IngredientsEditor";

const CATEGORIES = [
  "Main Dish", "Side", "Bread", "Beverage", "Dessert", "Appetizer",
  "Soup/Stew", "Salad", "Sauce/Condiment", "Snack", "Breakfast", "Other",
];
const DIFFICULTIES = ["Easy", "Medium", "Hard"];

interface FormState {
  title: string;
  authorSource: string;
  category: string;
  cuisine: string;
  prepTime: string;
  cookTime: string;
  totalTime: string;
  yieldServings: string;
  difficulty: string;
  directions: string;
  servingSuggestions: string;
  handwrittenNotes: string;
  transcriberNotes: string;
  ingredients: LocalIngredient[];
}

const EMPTY_FORM: FormState = {
  title: "",
  authorSource: "",
  category: "",
  cuisine: "",
  prepTime: "",
  cookTime: "",
  totalTime: "",
  yieldServings: "",
  difficulty: "",
  directions: "",
  servingSuggestions: "",
  handwrittenNotes: "",
  transcriberNotes: "",
  ingredients: [],
};

async function createRecipe(body: UpdateRecipeRequest): Promise<{ id: number; title: string }> {
  const res = await fetch("/api/recipes", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error((data as { error?: string }).error ?? "Failed to create recipe");
  }
  return res.json();
}

async function patchRecipe(id: number, body: UpdateRecipeRequest): Promise<void> {
  const res = await fetch(`/api/recipes/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error((data as { error?: string }).error ?? "Failed to save recipe");
  }
}

function formToBody(form: FormState): UpdateRecipeRequest {
  const body = {
    title: form.title.trim() || "Untitled Recipe",
    authorSource: form.authorSource || null,
    category: form.category || null,
    cuisine: form.cuisine || null,
    prepTime: form.prepTime || null,
    cookTime: form.cookTime || null,
    totalTime: form.totalTime || null,
    yieldServings: form.yieldServings || null,
    difficulty: form.difficulty || null,
    directions: form.directions || "",
    servingSuggestions: form.servingSuggestions || null,
    handwrittenNotes: form.handwrittenNotes || null,
    transcriberNotes: form.transcriberNotes || null,
    ingredients: form.ingredients.map((ing, idx) => ({
      sortOrder: ing.sortOrder ?? idx + 1,
      amount: ing.amount,
      unit: ing.unit,
      item: ing.item || (ing.sectionHeader ? "" : "Ingredient"),
      prepNotes: ing.prepNotes,
      sectionHeader: ing.sectionHeader,
    })),
  };
  // Validate against the canonical server schema before sending so we surface
  // shape drift here, not as a 400 from the API.
  return UpdateRecipeBody.parse(body);
}

export default function NewRecipePage() {
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [isDraftSaving, setIsDraftSaving] = useState(false);
  const [draftId, setDraftId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Track form dirtiness vs last-saved
  const draftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const draftCreatingRef = useRef(false);

  const setField = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Draft auto-save: when title is non-empty, save a draft after 2s debounce
  useEffect(() => {
    if (!form.title.trim()) return;

    if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    draftTimerRef.current = setTimeout(async () => {
      if (draftCreatingRef.current) return;
      const body = formToBody(form);

      if (!draftId) {
        // First save — create the draft
        draftCreatingRef.current = true;
        setIsDraftSaving(true);
        try {
          const created = await createRecipe(body);
          setDraftId(created.id);
          queryClient.invalidateQueries({ queryKey: getListRecipesQueryKey() });
        } catch {
          // Silently ignore draft-creation errors; user can still manually save
        } finally {
          setIsDraftSaving(false);
          draftCreatingRef.current = false;
        }
      } else {
        // Subsequent saves — patch the draft
        setIsDraftSaving(true);
        try {
          await patchRecipe(draftId, body);
          queryClient.invalidateQueries({ queryKey: getListRecipesQueryKey() });
        } catch {
          // Silently ignore
        } finally {
          setIsDraftSaving(false);
        }
      }
    }, 2000);

    return () => {
      if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    };
  }, [form, draftId, queryClient]);

  // Validation helpers (inline warning state — buttons remain enabled)
  const hasIngredients = form.ingredients.some(isRealIngredient);
  const hasDirections = form.directions.trim().length > 0;

  // Final save — redirect to edit page
  const handleSave = useCallback(async () => {
    if (!form.title.trim()) {
      setError("Please enter a recipe title before saving.");
      return;
    }
    const hasIngs = form.ingredients.some(isRealIngredient);
    const hasDir = form.directions.trim().length > 0;
    if (!hasIngs && !hasDir) {
      setError("Please add at least one ingredient or some directions before saving.");
      return;
    }
    setError(null);
    setIsSaving(true);

    try {
      // Cancel any pending draft timer
      if (draftTimerRef.current) clearTimeout(draftTimerRef.current);

      const body = formToBody(form);

      if (draftId) {
        // Patch the existing draft then navigate to edit
        await patchRecipe(draftId, body);
        queryClient.invalidateQueries({ queryKey: getListRecipesQueryKey() });
        toast({ title: `"${form.title.trim()}" saved — opening editor` });
        navigate(`/recipe/${draftId}/edit`);
      } else {
        // Create fresh
        const created = await createRecipe(body);
        queryClient.invalidateQueries({ queryKey: getListRecipesQueryKey() });
        toast({ title: `"${created.title}" created!` });
        navigate(`/recipe/${created.id}/edit`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
      setIsSaving(false);
    }
  }, [form, draftId, queryClient, navigate, toast]);

  return (
    <Layout>
      <div className="max-w-[720px] mx-auto">
        {/* ── Top bar ─────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 gap-3 flex-wrap">
          <Link
            href="/"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors group"
          >
            <ChevronLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" />
            Back to Cookbook
          </Link>

          <div className="flex items-center gap-2">
            {/* Draft badge */}
            {form.title.trim() && (
              <div className="flex items-center gap-1.5">
                {isDraftSaving ? (
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" /> Saving draft…
                  </span>
                ) : draftId ? (
                  <Badge variant="secondary" className="text-xs gap-1">
                    <FileEdit className="h-3 w-3" />
                    Draft saved
                  </Badge>
                ) : null}
              </div>
            )}

            <Button
              onClick={handleSave}
              disabled={isSaving}
              className="gap-1.5"
            >
              {isSaving
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <Save className="h-4 w-4" />}
              Save Recipe
            </Button>
          </div>
        </div>

        {/* ── Page heading ─────────────────────────────────────────────────── */}
        <div className="px-4 sm:px-6 pb-4 flex items-center gap-2">
          <PlusCircle className="w-4 h-4 text-muted-foreground" />
          <h1 className="text-sm font-medium text-muted-foreground uppercase tracking-widest">
            New Recipe
          </h1>
        </div>

        <ScrollArea className="flex-1">
          <div className="px-4 sm:px-6 pb-24 space-y-6">
            {/* Error banner */}
            {error && (
              <div className="flex items-start gap-3 bg-destructive/10 border border-destructive/30 rounded-lg px-4 py-3 text-sm text-destructive">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                {error}
              </div>
            )}

            {/* ── Title ───────────────────────────────────────────────────── */}
            <div>
              <Label className="text-xs text-muted-foreground">
                Title <span className="text-destructive">*</span>
              </Label>
              <Input
                value={form.title}
                onChange={(e) => setField("title", e.target.value)}
                className="mt-1 text-lg font-semibold font-serif"
                placeholder="Recipe title…"
                autoFocus
              />
            </div>

            {/* ── Author / Category / Cuisine ─────────────────────────────── */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs text-muted-foreground">Author / Source</Label>
                <Input
                  value={form.authorSource}
                  onChange={(e) => setField("authorSource", e.target.value)}
                  className="mt-1 text-sm"
                  placeholder="e.g. Aunt Carol"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Category</Label>
                <Select
                  value={form.category || "none"}
                  onValueChange={(v) => setField("category", v === "none" ? "" : v)}
                >
                  <SelectTrigger className="mt-1 h-9 text-sm">
                    <SelectValue placeholder="Category…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Cuisine</Label>
                <Input
                  list="cuisine-suggestions-new"
                  value={form.cuisine}
                  onChange={(e) => setField("cuisine", e.target.value)}
                  className="mt-1 text-sm"
                  placeholder="e.g. American"
                />
                <datalist id="cuisine-suggestions-new">
                  <option value="American" /><option value="Italian" /><option value="Mexican" />
                  <option value="Chinese" /><option value="Japanese" /><option value="French" />
                  <option value="Indian" /><option value="Thai" /><option value="Mediterranean" />
                  <option value="Greek" /><option value="Spanish" /><option value="Korean" />
                </datalist>
              </div>
            </div>

            {/* ── Times + Yield + Difficulty ──────────────────────────────── */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs text-muted-foreground">Prep Time</Label>
                <Input
                  value={form.prepTime}
                  onChange={(e) => setField("prepTime", e.target.value)}
                  className="mt-1 text-sm"
                  placeholder="15 min"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Cook Time</Label>
                <Input
                  value={form.cookTime}
                  onChange={(e) => setField("cookTime", e.target.value)}
                  className="mt-1 text-sm"
                  placeholder="30 min"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Total Time</Label>
                <Input
                  value={form.totalTime}
                  onChange={(e) => setField("totalTime", e.target.value)}
                  className="mt-1 text-sm"
                  placeholder="45 min"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Yield / Servings</Label>
                <Input
                  value={form.yieldServings}
                  onChange={(e) => setField("yieldServings", e.target.value)}
                  className="mt-1 text-sm"
                  placeholder="Serves 4"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Difficulty</Label>
                <Select
                  value={form.difficulty || "none"}
                  onValueChange={(v) => setField("difficulty", v === "none" ? "" : v)}
                >
                  <SelectTrigger className="mt-1 h-9 text-sm">
                    <SelectValue placeholder="Difficulty…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {DIFFICULTIES.map((d) => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* ── Ingredients ─────────────────────────────────────────────── */}
            <div>
              <Label className={`text-xs ${!hasIngredients && !hasDirections && form.title.trim() ? "text-amber-600" : "text-muted-foreground"}`}>
                Ingredients ({form.ingredients.length})
              </Label>
              <div className="mt-2">
                <IngredientsEditor
                  ingredients={form.ingredients}
                  onChange={(ings) => setField("ingredients", ings)}
                />
              </div>
            </div>

            {/* ── Directions ──────────────────────────────────────────────── */}
            <div>
              <Label className={`text-xs ${!hasIngredients && !hasDirections && form.title.trim() ? "text-amber-600" : "text-muted-foreground"}`}>
                Directions
              </Label>
              <Textarea
                value={form.directions}
                onChange={(e) => setField("directions", e.target.value)}
                className="mt-1 text-sm min-h-[180px] font-mono leading-relaxed"
                placeholder="Step-by-step instructions…"
              />
              {!hasIngredients && !hasDirections && form.title.trim() && (
                <p className="mt-1.5 text-xs text-amber-600">
                  At least one ingredient or some directions are required to save.
                </p>
              )}
            </div>

            {/* ── Serving suggestions ─────────────────────────────────────── */}
            <div>
              <Label className="text-xs text-muted-foreground">Serving Suggestions</Label>
              <Textarea
                value={form.servingSuggestions}
                onChange={(e) => setField("servingSuggestions", e.target.value)}
                className="mt-1 text-sm min-h-[60px]"
                placeholder="Optional…"
              />
            </div>

            {/* ── Notes ───────────────────────────────────────────────────── */}
            <div>
              <Label className="text-xs text-muted-foreground">
                Handwritten Notes <span className="italic">(from original)</span>
              </Label>
              <Textarea
                value={form.handwrittenNotes}
                onChange={(e) => setField("handwrittenNotes", e.target.value)}
                className="mt-1 text-sm min-h-[60px] italic bg-yellow-50 border-yellow-200 text-yellow-900 placeholder:text-yellow-400"
                placeholder="Jottings from the original card…"
              />
            </div>

            <div>
              <Label className="text-xs text-muted-foreground">
                Your Notes <span className="italic">(personal)</span>
              </Label>
              <Textarea
                value={form.transcriberNotes}
                onChange={(e) => setField("transcriberNotes", e.target.value)}
                className="mt-1 text-sm min-h-[60px]"
                placeholder="Your personal notes…"
              />
            </div>

            {/* ── Save ────────────────────────────────────────────────────── */}
            <div className="border-t pt-6 flex items-center justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => navigate("/")}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={isSaving}
                className="gap-1.5"
              >
                {isSaving
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <Save className="h-4 w-4" />}
                Save Recipe
              </Button>
            </div>
          </div>
        </ScrollArea>
      </div>
    </Layout>
  );
}
